import React from 'react';

/**
 * Interactive 27-Nakshatra Chakra Wheel — SVG.
 *
 * Layout: 27 sectors arranged clockwise (each 360/27 ≈ 13.33° of the wheel).
 * Sector 1 = Ashwini at the top (angle 0° pointing up), going clockwise.
 *
 * Highlights:
 *   - Birth nakshatra → gold glow
 *   - Lagna nakshatra → purple border
 *   - Planet glyphs plotted at their respective nakshatra sectors
 *
 * Props:
 *   - size: px size (default 360)
 *   - nakshatras: array of 27 metadata objects (must contain name_hi)
 *   - birthIdx: 0-26
 *   - lagnaIdx: 0-26
 *   - planets: { Sun: { nakshatra_idx: N, ... }, ... }
 *   - onClickNakshatra: (idx) => void
 *   - filteredPlanets: Set
 */

const PLANET_GLYPH = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};
const PLANET_COLOR = {
  Sun: '#fbbf24', Moon: '#e5e7eb', Mars: '#f87171', Mercury: '#86efac',
  Jupiter: '#fcd34d', Venus: '#f9a8d4', Saturn: '#94a3b8', Rahu: '#a78bfa', Ketu: '#f472b6',
};

// Helper: polar → cartesian (angle in radians, 0 = top, clockwise)
function polar(cx, cy, r, angleRad) {
  return {
    x: cx + r * Math.sin(angleRad),
    y: cy - r * Math.cos(angleRad),
  };
}

function sectorPath(cx, cy, rIn, rOut, startAng, endAng) {
  const p1 = polar(cx, cy, rOut, startAng);
  const p2 = polar(cx, cy, rOut, endAng);
  const p3 = polar(cx, cy, rIn, endAng);
  const p4 = polar(cx, cy, rIn, startAng);
  const large = endAng - startAng > Math.PI ? 1 : 0;
  return [
    `M ${p1.x} ${p1.y}`,
    `A ${rOut} ${rOut} 0 ${large} 1 ${p2.x} ${p2.y}`,
    `L ${p3.x} ${p3.y}`,
    `A ${rIn} ${rIn} 0 ${large} 0 ${p4.x} ${p4.y}`,
    'Z',
  ].join(' ');
}

export default function NakshatraWheel({
  size = 380,
  nakshatras = [],
  birthIdx = -1,
  lagnaIdx = -1,
  planets = {},
  onClickNakshatra,
  filteredPlanets,
}) {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size * 0.47;
  const rInner = size * 0.30;
  const rPlanet = size * 0.385;
  const rLabel = size * 0.385;

  const sectorAng = (2 * Math.PI) / 27;

  // Bucket planets by nakshatra_idx
  const planetsByNak = {};
  Object.entries(planets || {}).forEach(([name, info]) => {
    if (filteredPlanets && !filteredPlanets.has(name)) return;
    const idx = info.nakshatra_idx;
    if (!planetsByNak[idx]) planetsByNak[idx] = [];
    planetsByNak[idx].push(name);
  });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <defs>
        <radialGradient id="wheel-bg">
          <stop offset="0%" stopColor="rgba(76,29,149,0.30)" />
          <stop offset="100%" stopColor="rgba(15,23,42,0.40)" />
        </radialGradient>
        <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background disk */}
      <circle cx={cx} cy={cy} r={rOuter + 4} fill="url(#wheel-bg)" stroke="#FFD700" strokeWidth="1" opacity="0.95" />
      <circle cx={cx} cy={cy} r={rInner - 4} fill="rgba(15,23,42,0.6)" stroke="#FFD700" strokeWidth="0.6" opacity="0.6" />

      {/* 27 sectors */}
      {Array.from({ length: 27 }, (_, i) => i).map((i) => {
        const startAng = i * sectorAng;
        const endAng = (i + 1) * sectorAng;
        const midAng = (startAng + endAng) / 2;
        const isBirth = i === birthIdx;
        const isLagna = i === lagnaIdx;
        const isBoth = isBirth && isLagna;

        let fill = i % 2 === 0 ? 'rgba(76,29,149,0.18)' : 'rgba(76,29,149,0.08)';
        let stroke = 'rgba(255,215,0,0.35)';
        let strokeW = 0.5;
        if (isBirth) {
          fill = 'rgba(255,215,0,0.30)';
          stroke = '#FFD700';
          strokeW = 1.5;
        }
        if (isLagna) {
          stroke = '#a78bfa';
          strokeW = 2;
        }
        if (isBoth) {
          fill = 'rgba(255,215,0,0.40)';
        }

        const labelPos = polar(cx, cy, rLabel, midAng);
        const name = (nakshatras[i] && nakshatras[i].name_hi) || '';
        const fontSize = size * 0.025;

        return (
          <g key={`s-${i}`}>
            <path
              d={sectorPath(cx, cy, rInner, rOuter, startAng, endAng)}
              fill={fill}
              stroke={stroke}
              strokeWidth={strokeW}
              filter={isBirth ? 'url(#goldGlow)' : undefined}
              style={{ cursor: 'pointer', pointerEvents: 'all' }}
              onClick={() => onClickNakshatra && onClickNakshatra(i)}
            />
            <text
              x={labelPos.x}
              y={labelPos.y}
              fontSize={fontSize}
              fill={isBirth ? '#FFD700' : isLagna ? '#c4b5fd' : 'rgba(255,255,255,0.75)'}
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${(midAng * 180) / Math.PI}, ${labelPos.x}, ${labelPos.y})`}
              style={{ cursor: 'pointer', fontWeight: isBirth || isLagna ? 700 : 400 }}
              onClick={(e) => { e.stopPropagation(); onClickNakshatra && onClickNakshatra(i); }}
            >
              {name}
            </text>
          </g>
        );
      })}

      {/* Planet glyphs at their nakshatra */}
      {Object.entries(planetsByNak).map(([idxStr, names]) => {
        const idx = Number(idxStr);
        const midAng = (idx + 0.5) * sectorAng;
        return names.map((nm, j) => {
          // Stack planets slightly when multiple at same nakshatra
          const r = rPlanet - j * (size * 0.04);
          const pos = polar(cx, cy, r, midAng);
          return (
            <g key={`p-${idx}-${nm}`}>
              <circle cx={pos.x} cy={pos.y} r={size * 0.022} fill="rgba(15,23,42,0.85)" stroke={PLANET_COLOR[nm] || '#fff'} strokeWidth="1.2" />
              <text x={pos.x} y={pos.y} fontSize={size * 0.026} fill={PLANET_COLOR[nm] || '#fff'} textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: 'none', fontWeight: 700 }}>
                {PLANET_GLYPH[nm] || nm}
              </text>
            </g>
          );
        });
      })}

      {/* Center label */}
      <text x={cx} y={cy - 8} fontSize={size * 0.045} fill="#FFD700" textAnchor="middle" style={{ fontFamily: 'serif' }}>
        नक्षत्र
      </text>
      <text x={cx} y={cy + 14} fontSize={size * 0.035} fill="rgba(255,255,255,0.7)" textAnchor="middle">
        चक्र
      </text>
    </svg>
  );
}
